import { createFileRoute, Link } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";

import { TrustBand } from "@/components/site/TrustBand";
import { audiences } from "@/content/audiences";
import { ArrowRight, ArrowUpRight, Heart, Users, Hammer, Building2 } from "lucide-react";
import { EditorialHero } from "@/components/site/wow/EditorialHero";
import { AudienceMirror } from "@/components/site/wow/AudienceMirror";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { aeoGeo } from "@/lib/seo/schema";

const FOR_VEM_FAQ_IDS = [
  "sv_vilka_foretag_passar_fenny",
  "sv_hur_anvanda_video_b2b",
  "sv_hur_anvanda_film_industri",
];

export const Route = createFileRoute("/for-vem")({
  head: () => ({
    meta: [
      { title: "För vem är Fenny? Film för ert spår | Fenny" },
      {
        name: "description",
        content:
          "Film för grundare, marknadsteam, byråer och familjer som vill bevara kunskap och berättelser.",
      },
      { property: "og:title", content: "För vem är Fenny? Film | Fenny" },
      {
        property: "og:description",
        content: "Företag, privat och arv — film från samma team.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/for-vem" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/for-vem" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/for-vem", name: "För vem är Fenny? — Företag, privat & arv | Fenny", description: "Fenny passar grundare, marknadsteam, byråer, förbund och SoMe-team — plus brudpar, familjer och företag som vill bevara kunskap och arv på film."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          itemListElement: audiences.map((a, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: a.label,
            url: a.to,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(toFaqJsonLd(FOR_VEM_FAQ_IDS)),
      },
    ],
  }),
  component: ForVemPage,
});

function ForVemPage() {
  return (
    <div>
      <EditorialHero
        eyebrow="För vem"
        title="Företag, privat & arv."
        highlight={[2, 3]}
        tone="peach"
        lede="Fem företagsspår, tre privata och arvsspår — alla med både film från samma team. Många är flera samtidigt, och då bygger vi en hybrid. Klicka på spåret för att se er själva."
      />

      <AudienceMirror />

      <Section className="bg-surface/40">
        <EyebrowHeading eyebrow="Företag" title="Fem företagsspår" align="center" />
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {audiences.map((a) => (
            <Link
              key={a.slug}
              to="/for-vem/$slug"
              params={{ slug: a.slug }}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-peach/30"
            >
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                {a.label}
              </div>
              <h2 className="mt-3 text-2xl font-semibold leading-tight">
                {a.intro}
              </h2>
              <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-foreground/80 md:line-clamp-none">
                {a.longIntro}
              </p>

              <div className="mt-6 flex-1" />
              <div className="mt-4 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  Vi rekommenderar: <span className="text-foreground/90">{a.recommend}</span>
                </span>
                <ArrowRight className="h-4 w-4 text-foreground/60 transition-transform group-hover:translate-x-0.5" />
              </div>
            </Link>
          ))}
        </div>

        {/* B2B-vinkel på Heritage — kunskapsöverföring för företag */}
        <Link
          to="/heritage/kunskapsarv"
          className="group mt-8 flex flex-col gap-4 rounded-3xl border border-white/10 bg-gradient-to-br from-surface to-surface/40 p-7 transition-colors hover:border-peach/30 md:flex-row md:items-center md:justify-between"
        >
          <div className="flex items-start gap-4 md:items-center">
            <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-peach/15 text-peach ring-1 ring-peach/30">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">
                Även för företag — Heritage
              </div>
              <h3 className="mt-1 text-xl font-semibold leading-snug md:text-2xl">
                Kunskapsöverföring innan seniorerna går i pension
              </h3>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground/80">
                Fånga 30 års hantverk, beslut och tysta kunskap på film — innan den lämnar bolaget. Onboarding, utbildning och internkommunikation som faktiskt landar.
              </p>
            </div>
          </div>
          <span className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-peach">
            Läs mer
            <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </span>
        </Link>
      </Section>

      {/* Privat & arv */}
      <Section>
        <EyebrowHeading
          eyebrow="Privat & arv"
          title="När filmen handlar om människor, inte om varumärket"
          lede="Samma hantverk som våra varumärkesproduktioner — men för livets viktigaste stunder och berättelser värda att bevara."
          align="center"
        />
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {[
            {
              to: "/brollopsfilm" as const,
              icon: Heart,
              eyebrow: "Brudpar",
              title: "Cinematisk bröllopsfilm",
              body: "Vi tar 1–3 bröllop per år. Dokumentärt, filmiskt, ärligt — utan poser och utan stress.",
              cta: "Se bröllopsfilm",
            },
            {
              to: "/heritage/livsberattelser" as const,
              icon: Users,
              eyebrow: "Familjer",
              title: "Livsberättelser på film",
              body: "Mormor, morfar, mamma, pappa — deras historia bevarad för barnbarn som ännu inte är födda.",
              cta: "Se livsberättelser",
            },
            {
              to: "/heritage/kunskapsarv" as const,
              icon: Hammer,
              eyebrow: "Experter & yrkesveteraner",
              title: "Fånga kunskapen",
              body: "Hantverkare, forskare, ledare — kunskap som tar 30 år att bygga, bevarad på en eftermiddag.",
              cta: "Se kunskapsarv",
            },
          ].map((s) => (
            <Link
              key={s.eyebrow}
              to={s.to}
              className="group flex flex-col rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-peach/30"
            >
              <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-peach/15 text-peach ring-1 ring-peach/30">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-5 text-xs uppercase tracking-widest text-muted-foreground">
                {s.eyebrow}
              </div>
              <h3 className="mt-2 text-xl font-semibold leading-tight">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                {s.body}
              </p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-peach">
                {s.cta}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <AnswerFaq
        answerIds={FOR_VEM_FAQ_IDS}
        eyebrow="Frågor & svar"
        title="Vanliga frågor om vem Fenny passar"
      />


      <SignatureCta
        eyebrow="Osäker?"
        title="Vi säger ärligt vilket spår som passar."
        body="30 minuter, kostnadsfritt. Vi lyssnar på vad ni vill uppnå och pekar ut rätt väg — eller säger om vi inte är rätt partner just nu."
        tone="peach"
      />

      <TrustBand ctaLocation="for_vem_trust_band" />
    </div>
  );
}
